// React Hooks for EU Diseases
// Part of Policy Framework & EU Diseases Implementation

import { useState, useEffect } from 'react';
import euDiseaseService from '../services/euDiseaseService';

/**
 * Hook to fetch all EU notifiable diseases (mapped only)
 */
export function useEUDiseases(organizationId) {
  const [euDiseases, setEuDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  const loadEUDiseases = async () => {
    if (!organizationId) {
      setEuDiseases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const diseases = await euDiseaseService.getEUNotifiableDiseases(organizationId);
      const stats = await euDiseaseService.getEUDiseaseStatistics(organizationId);
      
      setEuDiseases(diseases);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading EU diseases:', err);
      setError(err.message);
      setEuDiseases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEUDiseases();
  }, [organizationId]);

  const refresh = () => {
    loadEUDiseases();
  };

  return { euDiseases, statistics, loading, error, refresh };
}

/**
 * Hook to fetch all EU diseases with mapping status (includes unmapped)
 */
export function useAllEUDiseases(organizationId) {
  const [allDiseases, setAllDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAllDiseases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const diseases = await euDiseaseService.getEUDiseasesWithMappingStatus(organizationId);
      setAllDiseases(diseases);
    } catch (err) {
      console.error('Error loading all EU diseases:', err);
      setError(err.message);
      setAllDiseases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllDiseases();
  }, [organizationId]);

  const refresh = () => {
    loadAllDiseases();
  };

  return { allDiseases, loading, error, refresh };
}

/**
 * Hook for EU disease mapping operations
 */
export function useEUDiseaseMapping() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mappingResults, setMappingResults] = useState(null);

  const runMapping = async (organizationId) => {
    try {
      setLoading(true);
      setError(null);
      setMappingResults(null);
      
      const results = await euDiseaseService.mapEUDiseasesToEntities(organizationId);
      setMappingResults(results);
      
      return results;
    } catch (err) {
      console.error('Error running EU disease mapping:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    runMapping,
    mappingResults,
    loading,
    error
  };
}

/**
 * Hook to get diseases requiring immediate reporting
 */
export function useImmediateReportingDiseases(organizationId) {
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDiseases = async () => {
    if (!organizationId) {
      setDiseases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await euDiseaseService.getImmediateReportingDiseases(organizationId);
      setDiseases(data);
    } catch (err) {
      console.error('Error loading immediate reporting diseases:', err);
      setError(err.message);
      setDiseases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiseases();
  }, [organizationId]);

  const refresh = () => {
    loadDiseases();
  };

  return { diseases, loading, error, refresh };
}

